import { Component } from 'react';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { ImgDown } from './Images/ImgDown.tsx';
import { ImgClose } from './Images/ImgClose.tsx';
import { ImgChevronLeft } from './Images/ImgChevronLeft.tsx';
import { ImgChevronRight } from './Images/ImgChevronRight.tsx';

interface ImageWrapperProps {
  src: string;
  fileList?: string[];

  className?: string;
  alt?: string;
  
  onPreviewSrc?: (src: string) => string;
  onViewSrc?: (src: string) => string;
  onDownloadSrc?: (src: string) => string;
  onAltText?: (src: string) => string;
}

function isTouchDevice() {
  return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
}

@observer
export class ImageWrapper extends Component<ImageWrapperProps> {
  @observable isFullscreen = false;
  @observable isLoading = true;
  @observable imageError = false;
  @observable zoomScale = 1;
  @observable translateX = 0;
  @observable translateY = 0;
  @observable currentImageIndex = 0;
  @observable isTransitioningImage = false;
  @observable showNavButtons = false;
  @observable isLoadingFullscreen = false;
  @observable nextImageSrc: string | null = null;
  @observable nextImageAlt: string | null = null;
  @observable isDownloading = false;

  private preloadCache = new Map<string, HTMLImageElement>();

  private initialDistance = 0;
  private initialScale = 1;
  private imageRect: DOMRect | null = null;
  private isPinching = false;
  private isTransitioning = false;
  private transformOrigin = 'center center';
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private isSwiping = false;

  @action
  openFullscreen = () => {
    const { src, fileList } = this.props;
    if (fileList && fileList.length > 0) {
      this.currentImageIndex = fileList.indexOf(src);
      if (this.currentImageIndex === -1) this.currentImageIndex = 0;
    }
    this.isFullscreen = true;
    this.isLoadingFullscreen = true;
    document.body.style.overflow = 'hidden';
  };

  @action
  closeFullscreen = () => {
    this.isFullscreen = false;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.currentImageIndex = 0;
    document.body.style.overflow = 'unset';
  };

  @action
  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  @action
  setImageError = (error: boolean) => {
    this.imageError = error;
  };

  @action
  setZoom = (scale: number, x: number = 0, y: number = 0) => {
    this.zoomScale = scale;
    this.translateX = x;
    this.translateY = y;
  };

  @action
  resetZoom = () => {
    this.isTransitioning = true;
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  centerZoom = () => {
    this.isTransitioning = true;
    this.translateX = 0;
    this.translateY = 0;
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300);
  };

  @action
  navigateImage = (direction: 'prev' | 'next') => {
    const { fileList } = this.props;
    if (!fileList || fileList.length <= 1) return;

    // Reset zoom immediately without transition
    this.zoomScale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isTransitioning = false;

    // Calculate next index
    let nextIndex: number;
    if (direction === 'next') {
      nextIndex = (this.currentImageIndex + 1) % fileList.length;
    } else {
      nextIndex = (this.currentImageIndex - 1 + fileList.length) % fileList.length;
    }

    // Preload next image
    let nextSrc = fileList[nextIndex];
    if (this.props.onViewSrc) {
      nextSrc = this.props.onViewSrc(nextSrc);
    }
    this.nextImageSrc = nextSrc;

    // Check if already preloaded - skip loading state if so
    const cachedImg = this.getPreloadedImage(nextSrc);
    if (cachedImg) {
      this.setLoadingFullscreen(false);
      // Start cross-fade immediately
      this.isTransitioningImage = true;
      setTimeout(() => {
        this.currentImageIndex = nextIndex;
        this.isTransitioningImage = false;
        requestAnimationFrame(() => {
          this.nextImageSrc = null;
        });
      }, 300);
    } else {
      // Not cached, preload normally
      this.setLoadingFullscreen(true);

      const img = new Image();
      img.onload = () => {
        this.preloadCache.set(nextSrc, img);
        this.setLoadingFullscreen(false);
        // Start cross-fade: fade out current, fade in next
        this.isTransitioningImage = true;
        
        // After fade completes, swap the images
        setTimeout(() => {
          // Update index while BOTH images are still rendered
          this.currentImageIndex = nextIndex;
          // End transition to reset opacities, but keep nextImageSrc for one more frame
          this.isTransitioningImage = false;
          
          // Wait for next frame before clearing nextImageSrc
          requestAnimationFrame(() => {
            this.nextImageSrc = null;
          });
        }, 300);
      };
      img.onerror = () => {
        this.setLoadingFullscreen(false);
        // Still change image even if preload fails
        this.isTransitioningImage = true;
        setTimeout(() => {
          this.currentImageIndex = nextIndex;
          this.isTransitioningImage = false;
          requestAnimationFrame(() => {
            this.nextImageSrc = null;
          });
        }, 300);
      };
      img.src = nextSrc;
    }
  };

  @action
  setShowNavButtons = (show: boolean) => {
    this.showNavButtons = show;
  };

  @action
  setLoadingFullscreen = (loading: boolean) => {
    this.isLoadingFullscreen = loading;
  };

  private preloadNextImage = () => {
    const { fileList } = this.props;
    if (!fileList || fileList.length <= 1) return;

    // Preload next image in sequence
    const nextIndex = (this.currentImageIndex + 1) % fileList.length;
    let nextSrc = fileList[nextIndex];
    if (this.props.onViewSrc) {
      nextSrc = this.props.onViewSrc(nextSrc);
    }

    // Skip if already cached
    if (this.preloadCache.has(nextSrc)) return;

    const img = new Image();
    img.onload = () => {
      this.preloadCache.set(nextSrc, img);
      // Continue preloading the one after next
      requestAnimationFrame(() => {
        const followingIndex = (nextIndex + 1) % fileList.length;
        let followingSrc = fileList[followingIndex];
        if (this.props.onViewSrc) {
          followingSrc = this.props.onViewSrc(followingSrc);
        }
        if (!this.preloadCache.has(followingSrc)) {
          const img2 = new Image();
          img2.src = followingSrc;
          img2.onload = () => {
            this.preloadCache.set(followingSrc, img2);
          };
        }
      });
    };
    img.src = nextSrc;
  };

  private getPreloadedImage = (src: string): HTMLImageElement | undefined => {
    return this.preloadCache.get(src);
  };

  @action
  setDownloading = (downloading: boolean) => {
    this.isDownloading = downloading;
  };

  handleImageLoad = () => {
    this.setLoading(false);
    this.setImageError(false);
  };

  handleImageError = () => {
    this.setLoading(false);
    this.setImageError(true);
  };

  handleFullscreenImageLoad = () => {
    this.setLoadingFullscreen(false);
    // Preload next image now that current one is loaded
    this.preloadNextImage();
  };

  handleKeyDown = (e: KeyboardEvent) => {
    if (!this.isFullscreen) return;
    
    if (e.key === 'Escape') {
      this.closeFullscreen();
    } else if (e.key === 'ArrowLeft') {
      this.navigateImage('prev');
    } else if (e.key === 'ArrowRight') {
      this.navigateImage('next');
    }
  };

  getTouchDistance = (touches: React.TouchList): number => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  getTouchCenter = (touches: React.TouchList): { x: number; y: number } => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;
    
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      this.isPinching = true;
      this.isSwiping = false;
      this.initialDistance = this.getTouchDistance(e.touches);
      this.initialScale = this.zoomScale;
      
      this.imageRect = e.currentTarget.getBoundingClientRect();
      const touchCenter = this.getTouchCenter(e.touches);
      
      const originX = touchCenter.x - this.imageRect.left;
      const originY = touchCenter.y - this.imageRect.top;
      this.transformOrigin = `${originX}px ${originY}px`;
    } else if (e.touches.length === 1) {
      // Single touch - potential swipe
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartTime = Date.now();
      this.isSwiping = true;
    }
  };

  handleTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isTransitioning) return;
    
    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = this.getTouchDistance(e.touches);
      let scale = (currentDistance / this.initialDistance) * this.initialScale;
      scale = Math.max(1, Math.min(scale, 5));
      this.setZoom(scale);
    } else if (this.isSwiping && e.touches.length === 1) {
      const deltaX = Math.abs(e.touches[0].clientX - this.touchStartX);
      const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);
      
      // If moving more horizontally than vertically, prevent default
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  handleTouchEnd = (e: React.TouchEvent<HTMLImageElement>) => {
    if (this.isPinching) {
      this.isPinching = false;
      this.resetZoom();
      setTimeout(() => {
        this.transformOrigin = 'center center';
      }, 300);
    } else if (this.isSwiping) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndTime = Date.now();
      
      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;
      const deltaTime = touchEndTime - this.touchStartTime;
      
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const velocity = distance / deltaTime;
      
      // Swipe detection: minimum distance and velocity, and more horizontal than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && velocity > 0.3) {
        const { fileList } = this.props;
        if (fileList && fileList.length > 1) {
          if (deltaX > 0) {
            // Swipe right - go to previous
            this.navigateImage('prev');
          } else {
            // Swipe left - go to next
            this.navigateImage('next');
          }
        }
      }
      
      this.isSwiping = false;
    }
  };

  handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    const { fileList } = this.props;
    if (fileList && fileList.length > 1) {
      this.navigateImage('next');
    }
  };

  @action.bound
  downloadImage = async () => {  

    const { fileList, onDownloadSrc } = this.props;
    let imageUrl = fileList && fileList.length > 0 
      ? fileList[this.currentImageIndex] 
      : this.props.src;

    if (onDownloadSrc) {
      imageUrl = onDownloadSrc(imageUrl);
    }
    
    this.setDownloading(true);
    try {
      const response = await fetch(imageUrl);
      if (!response.ok){
        throw new Error(`Network response is ${response.status}: ` + response.statusText);
      }
      
      const blob = await response.blob();
      // get filename from content-disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `image-${Date.now()}.jpg`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      this.setDownloading(false);
    }
  };

  getCurrentImageSrc = () => {
    const { fileList, src } = this.props;

    let result = src;

    if (fileList && fileList.length > 0) {
      result = fileList[this.currentImageIndex];
    }

    if (this.props.onViewSrc) {
      result = this.props.onViewSrc(result);
    }

    return result;
  };
  
  getCurrentImageAlt = () => {
    const { fileList, src } = this.props;

    let result = src;

    if (fileList && fileList.length > 0) {
      result = fileList[this.currentImageIndex];
    }

    if (this.props.onAltText) {
      result = this.props.onAltText(result);
    }

    return result;
  };

  constructor(props: ImageWrapperProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'unset';
  }

  render() {
    const { src, onPreviewSrc, onDownloadSrc, className = '', alt = 'Image', fileList } = this.props;
    const hasGallery = fileList && fileList.length > 1;

    let previewSrc = src;
    if (onPreviewSrc) {
      previewSrc = onPreviewSrc(src);
    }
    
    let downloadImageUrl = fileList && fileList.length > 0 
      ? fileList[this.currentImageIndex] 
      : this.props.src;

    if (onDownloadSrc) {
      downloadImageUrl = onDownloadSrc(downloadImageUrl);
    }

    return (
      <>
        {/* Thumbnail Image */}
        <span className={`image-wrapper ${className}`} style={{ position: 'relative', display: 'inline-block' }}>
          {this.isLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid color-mix(in srgb, var(--text-primary) 10%, transparent)',
                  borderTop: '3px solid color-mix(in srgb, var(--text-primary) 40%, transparent)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            </div>
          )}
          <img
            src={previewSrc}
            alt={alt}
            className="thumbnail-image"
            loading="lazy"
            onClick={this.openFullscreen}
            onLoad={this.handleImageLoad}
            onError={this.handleImageError}
            style={{
              cursor: 'pointer',
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '8px',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              opacity: this.isLoading ? 0.5 : 1,
              fontSize: '12px'
            }}
            onMouseEnter={(e) => {
              if (!this.isLoading) {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = 'var(--shadow)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </span>

        {/* Fullscreen Modal */}
        {this.isFullscreen && (
          <div 
            className="fullscreen-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                this.closeFullscreen();
              }
            }}
            onMouseMove={() => {
              if (hasGallery) this.setShowNavButtons(true);
            }}
            onMouseLeave={() => {
              if (hasGallery) this.setShowNavButtons(false);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              animation: 'fadeIn 0.3s ease'
            }}
          >
            {/* Controls Bar */}
            <div 
              className="fullscreen-controls"
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                display: 'flex',
                gap: '12px',
                zIndex: 2001
              }}
            >
              <button
                onClick={this.downloadImage}
                className="control-button"
                title="Download Image"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'white',
                  cursor: this.isDownloading ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '18px',
                  width: '48px',
                  height: '48px',
                  opacity: this.isDownloading ? 0.6 : 1,
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!this.isDownloading) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {this.isDownloading ? (
                  <span
                    style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid color-mix(in srgb, var(--text-primary) 20%, transparent)',
                      borderTop: '2px solid var(--text-primary)',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}
                  />
                ) : (
                  <ImgDown size={20} />
                )}
              </button>
              <button
                onClick={this.closeFullscreen}
                className="control-button"
                title="Close (ESC)"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '20px',
                  width: '48px',
                  height: '48px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <ImgClose size={20} />
              </button>
            </div>

            {/* Gallery Counter */}
            {hasGallery && (
              <div 
                style={{
                  position: 'fixed',
                  top: '20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  zIndex: 2001,
                  backdropFilter: 'blur(10px)'
                }}
              >
                {this.currentImageIndex + 1} / {fileList.length}
              </div>
            )}

            {/* Navigation Buttons */}
            {hasGallery && (
              <>
                {/* Previous Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    this.navigateImage('prev');
                  }}
                  style={{
                    position: 'fixed',
                    left: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    zIndex: 2001,
                    opacity: this.showNavButtons || isTouchDevice() ? 1 : 0,
                    transition: 'all 0.3s ease',
                    pointerEvents: this.showNavButtons || isTouchDevice() ? 'auto' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                  }}
                >
                  <ImgChevronLeft size={24} />
                </button>

                {/* Next Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    this.navigateImage('next');
                  }}
                  style={{
                    position: 'fixed',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '56px',
                    height: '56px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    zIndex: 2001,
                    opacity: this.showNavButtons || isTouchDevice() ? 1 : 0,
                    transition: 'all 0.3s ease',
                    pointerEvents: this.showNavButtons || isTouchDevice() ? 'auto' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                  }}
                >
                  <ImgChevronRight size={24} />
                </button>
              </>
            )}

            {/* Full Size Image Container with Cross-fade */}
            <div 
              className="fullscreen-image-container"
              style={{
                maxWidth: '95vw',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
                position: 'relative'
              }}
            >
              {/* Loading Spinner */}
              {this.isLoadingFullscreen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2002,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      border: '4px solid color-mix(in srgb, var(--text-primary) 20%, transparent)',
                      borderTop: '4px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}
                  />
                </div>
              )}

              {/* Next Image (bottom layer for cross-fade) */}
              {this.nextImageSrc && (
                <img
                  src={this.nextImageSrc}
                  alt={this.nextImageAlt || ''}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100vh',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    opacity: this.isTransitioningImage ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                    cursor: hasGallery ? 'pointer' : 'default'
                  }}
                />
              )}

              {/* Current Image (top layer for cross-fade) */}
              <img
                src={this.getCurrentImageSrc()}
                alt={this.getCurrentImageAlt()}
                onLoad={this.handleFullscreenImageLoad}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  transform: `scale(${this.zoomScale})`,
                  transformOrigin: this.transformOrigin,
                  opacity: this.isTransitioningImage ? 0 : 1,
                  transition: this.isTransitioningImage ? 'opacity 0.3s ease' : undefined,
                  position: 'relative',
                  zIndex: 2,
                  cursor: hasGallery ? 'pointer' : 'default'
                }}
                onClick={this.handleImageClick}
                onTouchStart={this.handleTouchStart}
                onTouchMove={this.handleTouchMove}
                onTouchEnd={this.handleTouchEnd}
              />
            </div>

            {/* Instructions */}
            <div 
              style={{
                position: 'fixed',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                opacity: 0.7,
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 2
              }}
            >
              {!isTouchDevice() ? (
                hasGallery 
                  ? 'Click image, use arrow keys or buttons to navigate • Press ESC to close'
                  : 'Tap outside image or press ESC to close'
              ) : (
                hasGallery
                  ? 'Tap image or swipe left/right to navigate • Tap outside to close'
                  : 'Tap outside image to close'
              )}
            </div>
          </div>
        )}

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes zoomIn {
            from { 
              opacity: 0;
              transform: scale(0.95);
            }
            to { 
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }
}